import React, { useEffect, useMemo, useState } from 'react';
import { fitnessStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { ratingStrings } from '../../content/ratings';
import { validateFitnessForm } from '../../validation/hobbies';
import useStepperFlow from '../../hooks/useStepperFlow';
import { showFirstStepWithErrors } from '../../utils/formStepErrors';
import FitnessExerciseEditor, { createEmptyExercise, normalizeExercise } from './FitnessExerciseEditor';

/**
 * Performs the today operation.
 *
 * @returns {void} No value; the operation applies its documented side effects.
 */
const today = () => new Date().toISOString().slice(0, 10);

const structuredTypes = ['Strength', 'Yoga', 'Pilates', 'Bodyweight', 'Class', 'Mobility', 'Other'];
const sessionTypes = ['Walk', 'Run', 'Cycling', 'Swim', 'Sport', 'Cardio', 'Other'];
const statuses = ['Planned', 'Completed', 'Skipped'];
const intensities = ['Easy', 'Moderate', 'Hard', 'Max effort'];
const stepErrorFields = {
  basics: ['title', 'mode', 'workoutType', 'activityType', 'status', 'workoutDate', 'scheduledAt', 'plannedDate', 'completedAt', 'completedDate'],
  details: ['durationMinutes', 'intensity', 'caloriesBurned', 'distance', 'distanceUnit', 'pace', 'averageHeartRate', 'maxHeartRate', 'elevationGain', 'equipment', 'muscleGroups', 'focusAreas', 'className', 'location', 'coach'],
  log: ['strengthExercises'],
  review: ['energyLevel', 'sorenessLevel', 'recoveryNotes', 'rating', 'review', 'notes', 'tags'],
};

const emptyWorkout = {
  id: null,
  title: '',
  mode: 'structured',
  workoutType: 'Strength',
  workoutDate: today(),
  scheduledAt: '',
  completedAt: today(),
  activityType: 'Strength',
  durationMinutes: '',
  intensity: 'Moderate',
  caloriesBurned: '',
  distance: '',
  distanceUnit: 'mi',
  pace: '',
  averageHeartRate: '',
  maxHeartRate: '',
  elevationGain: '',
  equipment: '',
  muscleGroups: '',
  strengthExercises: [createEmptyExercise()],
  focusAreas: '',
  className: '',
  location: '',
  coach: '',
  status: 'Completed',
  plannedDate: '',
  completedDate: '',
  planName: '',
  templateName: '',
  energyLevel: '',
  sorenessLevel: '',
  recoveryNotes: '',
  rating: '',
  review: '',
  notes: '',
  tags: '',
  favorite: false,
};

/**
 * Normalizes array.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function formatArray(value) {
  return Array.isArray(value) ? value.join(', ') : value || '';
}

/**
 * Normalizes type.
 *
 * @param {*} mode - mode value.
 * @param {string} workoutType - workout type value.
 * @returns {*} The derived operation result.
 */
function normalizeType(mode, workoutType) {
  const options = mode === 'session' ? sessionTypes : structuredTypes;
  return options.includes(workoutType) ? workoutType : options[0];
}

/**
 * Creates initial form.
 *
 * @param {*} initialData - initial data value.
 * @returns {*} The derived operation result.
 */
function createInitialForm(initialData) {
  if (!initialData) return { ...emptyWorkout };
  const mode = initialData.mode || (['Walk', 'Run', 'Cycling', 'Swim', 'Sport', 'Cardio'].includes(initialData.workoutType) ? 'session' : 'structured');
  const workoutType = normalizeType(mode, initialData.workoutType || emptyWorkout.workoutType);
  return {
    ...emptyWorkout,
    ...initialData,
    mode,
    workoutType,
    activityType: initialData.activityType || workoutType,
    scheduledAt: initialData.scheduledAt || initialData.plannedDate || '',
    completedAt: initialData.completedAt || initialData.completedDate || (initialData.status === 'Completed' ? initialData.workoutDate : ''),
    durationMinutes: initialData.durationMinutes || '',
    caloriesBurned: initialData.caloriesBurned || '',
    averageHeartRate: initialData.averageHeartRate || '',
    maxHeartRate: initialData.maxHeartRate || '',
    elevationGain: initialData.elevationGain || '',
    equipment: formatArray(initialData.equipment),
    muscleGroups: formatArray(initialData.muscleGroups),
    strengthExercises: Array.isArray(initialData.strengthExercises) && initialData.strengthExercises.length
      ? initialData.strengthExercises.map(normalizeExercise)
      : [createEmptyExercise()],
    focusAreas: formatArray(initialData.focusAreas),
    tags: formatArray(initialData.tags),
    rating: initialData.rating || '',
    review: initialData.review || '',
  };
}

/**
 * Renders the star rating experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function StarRating({ value, onChange }) {
  const rating = Number(value || 0);
  const stars = Array.from({ length: 5 }, (_, index) => index + 1);
  /**
   * Retrieves star state.
   *
   * @param {*} star - star value.
   * @returns {*} The derived operation result.
   */
  const getStarState = (star) => {
    if (rating >= star) return 'full';
    if (rating >= star - 0.5) return 'half';
    return 'empty';
  };

  return (
    <div className="star-rating-wrap">
      <div className="star-rating" role="radiogroup" aria-label={fitnessStrings.form.ratingAriaLabel}>
        {stars.map((star) => (
          <span className={`star-button ${getStarState(star)}`} key={star}>
            <span className="star-icon" aria-hidden="true">★</span>
            <button type="button" className="star-hit star-hit-left" aria-label={`${star - 0.5} stars`} aria-pressed={rating === star - 0.5} onClick={() => onChange(String(star - 0.5))} />
            <button type="button" className="star-hit star-hit-right" aria-label={`${star} stars`} aria-pressed={rating === star} onClick={() => onChange(String(star))} />
          </span>
        ))}
      </div>
      <div className="star-rating-summary">
        <span>{rating ? ratingStrings.value(rating) : ratingStrings.notRated}</span>
        {rating > 0 && <button type="button" className="btn-ghost compact-btn" onClick={() => onChange('')}>{fitnessStrings.form.actions.clearRating}</button>}
      </div>
    </div>
  );
}

/** Collects a manual activity or structured workout and emits a normalized fitness draft. */
export default function FitnessForm({ theme, onSave, onCancel, isSaving, initialData, onDirtyChange, exerciseCatalog = [] }) {
  const initialForm = useMemo(() => createInitialForm(initialData), [initialData]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const isSession = form.mode === 'session';
  const isStructured = !isSession;
  const typeOptions = isSession ? sessionTypes : structuredTypes;
  const dateLabel = form.status === 'Completed' ? 'Completed on' : 'Planned for';
  const dateField = form.status === 'Completed' ? 'completedAt' : 'scheduledAt';
  const steps = useMemo(() => ['basics', 'details', 'log', 'review'], []);
  const {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  } = useStepperFlow(steps, 'basics');

  useEffect(() => {
    setForm(initialForm);
    resetStepper('basics');
  }, [initialForm, resetStepper]);

  useEffect(() => {
    onDirtyChange?.(JSON.stringify(form) !== JSON.stringify(initialForm));
  }, [form, initialForm, onDirtyChange]);

  /**
   * Updates field.
   *
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateField = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'mode') {
        next.workoutType = normalizeType(value, current.workoutType);
        next.activityType = next.workoutType;
      }
      if (field === 'workoutType') {
        next.activityType = value;
      }
      return next;
    });
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (canGoNext) {
      goToNextStep();
      return;
    }
    if (!isStepSettled) return;
    const validation = validateFitnessForm(form);
    setErrors(validation.fieldErrors);
    if (!validation.valid) {
      showFirstStepWithErrors(validation.fieldErrors, stepErrorFields, 'basics', goToSection);
      return;
    }
    await onSave({ ...form, notes: '' });
  };

  return (
    <form className="recipe-form fitness-form" onSubmit={(event) => event.preventDefault()} noValidate>
      <div className="hobby-flow-stepper" role="tablist" aria-label="Fitness editor steps">
        {[
          ['basics', fitnessStrings.form.sections.basics],
          ['details', fitnessStrings.form.sections.details],
          ['log', isStructured ? fitnessStrings.form.sections.exercises : fitnessStrings.form.sections.sessionLog],
          ['review', fitnessStrings.form.sections.review],
        ].map(([section, label]) => (
          <button
            key={section}
            type="button"
            className={activeSection === section ? 'active' : steps.indexOf(section) < activeStepIndex ? 'complete' : ''}
            onClick={() => goToSection(section)}
            role="tab"
            aria-selected={activeSection === section}
          >
            <span>{steps.indexOf(section) + 1}</span>
            {label}
          </button>
        ))}
      </div>

      {activeSection === 'basics' && (
      <section className="recipe-form-section priority-section">
        <h3>{fitnessStrings.form.sections.basics}</h3>
        <div className="form-grid three-columns">
          <div className="form-group form-grid-full">
            <label>{fitnessStrings.fields.mode}</label>
            <div className="segmented-control">
              <button type="button" className={isStructured ? 'active' : ''} onClick={() => updateField('mode', 'structured')}>{fitnessStrings.form.modes.structured}</button>
              <button type="button" className={isSession ? 'active' : ''} onClick={() => updateField('mode', 'session')}>{fitnessStrings.form.modes.session}</button>
            </div>
          </div>
          <div className="form-group">
            <label>{fitnessStrings.fields.title} <span className="required-mark">{moduleCommonStrings.labels.required}</span></label>
            <input value={form.title} onChange={(event) => updateField('title', event.target.value)} placeholder={isSession ? fitnessStrings.placeholders.sessionTitle : fitnessStrings.placeholders.structuredTitle} aria-invalid={Boolean(errors.title)} />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>
          <div className="form-group">
            <label>{fitnessStrings.fields.type}</label>
            <select value={form.workoutType} onChange={(event) => updateField('workoutType', event.target.value)} aria-invalid={Boolean(errors.workoutType)}>
              {typeOptions.map((type) => <option key={type}>{type}</option>)}
            </select>
            {errors.workoutType && <p className="field-error">{errors.workoutType}</p>}
          </div>
          <div className="form-group">
            <label>{fitnessStrings.fields.status}</label>
            <select value={form.status} onChange={(event) => updateField('status', event.target.value)}>
              {statuses.map((status) => <option key={status}>{status}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{dateLabel}</label>
            <input type="date" value={form[dateField]} onChange={(event) => updateField(dateField, event.target.value)} aria-invalid={Boolean(errors[dateField])} />
            {errors[dateField] && <p className="field-error">{errors[dateField]}</p>}
          </div>
          <div className="form-group">
            <label>{fitnessStrings.fields.intensity}</label>
            <select value={form.intensity} onChange={(event) => updateField('intensity', event.target.value)} aria-invalid={Boolean(errors.intensity)}>
              {intensities.map((intensity) => <option key={intensity}>{intensity}</option>)}
            </select>
            {errors.intensity && <p className="field-error">{errors.intensity}</p>}
          </div>

          <div className="form-group form-grid-full">
            <button
              type="button"
              className={`favorite-toggle inline-favorite-toggle ${form.favorite ? 'active' : ''}`}
              onClick={() => updateField('favorite', !form.favorite)}
              aria-pressed={form.favorite}
            >
              <span>{form.favorite ? fitnessStrings.form.favorite : fitnessStrings.form.favoriteAction}</span>
            </button>
          </div>
        </div>
      </section>
      )}

      {activeSection === 'details' && (
      <>
      <section className="recipe-form-section">
        <h3>{fitnessStrings.form.sections.timeAndEffort}</h3>
        <div className="form-grid three-columns">
          <div className="form-group">
            <label>{fitnessStrings.fields.duration}</label>
            <input type="number" min="0" value={form.durationMinutes} onChange={(event) => updateField('durationMinutes', event.target.value)} placeholder={fitnessStrings.placeholders.duration} aria-invalid={Boolean(errors.durationMinutes)} />
            {errors.durationMinutes && <p className="field-error">{errors.durationMinutes}</p>}
          </div>
          <div className="form-group">
            <label>{fitnessStrings.fields.calories}</label>
            <input type="number" min="0" value={form.caloriesBurned} onChange={(event) => updateField('caloriesBurned', event.target.value)} placeholder={fitnessStrings.placeholders.calories} aria-invalid={Boolean(errors.caloriesBurned)} />
            {errors.caloriesBurned && <p className="field-error">{errors.caloriesBurned}</p>}
          </div>

        </div>
      </section>

      {isSession && (
        <section className="recipe-form-section">
          <h3>{fitnessStrings.form.sections.activityMetrics}</h3>
          <div className="form-grid three-columns">
            <div className="form-group"><label>{fitnessStrings.fields.distance}</label><input value={form.distance} onChange={(event) => updateField('distance', event.target.value)} placeholder={fitnessStrings.placeholders.distance} /></div>
            <div className="form-group"><label>{fitnessStrings.fields.unit}</label><select value={form.distanceUnit} onChange={(event) => updateField('distanceUnit', event.target.value)}><option>mi</option><option>km</option></select></div>
            <div className="form-group"><label>{fitnessStrings.fields.averageHeartRate}</label><input type="number" min="0" value={form.averageHeartRate} onChange={(event) => updateField('averageHeartRate', event.target.value)} placeholder={fitnessStrings.placeholders.averageHeartRate} /></div>
            <div className="form-group"><label>{fitnessStrings.fields.maxHeartRate}</label><input type="number" min="0" value={form.maxHeartRate} onChange={(event) => updateField('maxHeartRate', event.target.value)} placeholder={fitnessStrings.placeholders.maxHeartRate} /></div>
            <div className="form-group"><label>{fitnessStrings.fields.elevationGain}</label><input type="number" min="0" value={form.elevationGain} onChange={(event) => updateField('elevationGain', event.target.value)} placeholder={fitnessStrings.placeholders.elevationGain} /></div>
          </div>
        </section>
      )}

      {isStructured && (
        <section className="recipe-form-section">
          <h3>{fitnessStrings.form.sections.workoutFocus}</h3>
          <div className="form-grid three-columns">
            <>
              {['Yoga', 'Pilates', 'Mobility', 'Class'].includes(form.workoutType) && (
                <div className="form-group"><label>{fitnessStrings.fields.focusAreas}</label><input value={form.focusAreas} onChange={(event) => updateField('focusAreas', event.target.value)} placeholder={fitnessStrings.placeholders.focusAreas} /></div>
              )}
              {['Sport', 'Class'].includes(form.workoutType) && (
                <>
                  <div className="form-group"><label>{fitnessStrings.fields.classOrSport}</label><input value={form.className} onChange={(event) => updateField('className', event.target.value)} placeholder={fitnessStrings.placeholders.classOrSport} /></div>
                  <div className="form-group"><label>{fitnessStrings.fields.location}</label><input value={form.location} onChange={(event) => updateField('location', event.target.value)} placeholder={fitnessStrings.placeholders.location} /></div>
                  <div className="form-group"><label>{fitnessStrings.fields.coach}</label><input value={form.coach} onChange={(event) => updateField('coach', event.target.value)} placeholder={fitnessStrings.placeholders.coach} /></div>
                </>
              )}
            </>
          </div>
        </section>
      )}

      <section className="recipe-form-section">
        <h3>{fitnessStrings.form.sections.tags}</h3>
        <div className="form-grid">
          <div className="form-group form-grid-full">
            <label>{fitnessStrings.fields.tags}</label>
            <input value={form.tags} onChange={(event) => updateField('tags', event.target.value)} placeholder={fitnessStrings.placeholders.tags} />
          </div>
        </div>
      </section>
      </>
      )}

      {activeSection === 'log' && (
      isStructured ? (
        <section className="recipe-form-section">
          <h3>{fitnessStrings.form.sections.exercises}</h3>
          <FitnessExerciseEditor exercises={form.strengthExercises} catalog={exerciseCatalog} onChange={(strengthExercises) => setForm((current) => ({ ...current, strengthExercises }))} />
        </section>
      ) : (
        <section className="recipe-form-section">
          <h3>{fitnessStrings.form.sections.sessionLog}</h3>
          <div className="form-grid">
            <div className="form-group"><label>{fitnessStrings.fields.routeOrLocation}</label><input value={form.location} onChange={(event) => updateField('location', event.target.value)} placeholder={fitnessStrings.placeholders.routeOrLocation} /></div>
            <div className="form-group"><label>{fitnessStrings.fields.pace}</label><input value={form.pace} onChange={(event) => updateField('pace', event.target.value)} placeholder={fitnessStrings.placeholders.pace} /></div>
          </div>
        </section>
      )
      )}

      {activeSection === 'review' && (
      <section className="recipe-form-section">
        <h3>{fitnessStrings.form.sections.review}</h3>
        <div className="form-grid">
          <div className="form-group form-grid-full">
            <label>{fitnessStrings.fields.rating}</label>
            <StarRating value={form.rating} onChange={(rating) => updateField('rating', rating)} />
            {errors.rating && <p className="field-error">{errors.rating}</p>}
          </div>
          <div className="form-group form-grid-full"><label>{fitnessStrings.fields.review}</label><textarea rows="4" value={form.review} onChange={(event) => updateField('review', event.target.value)} /></div>
        </div>
      </section>
      )}

      <div className="form-actions sticky-actions">
        <button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>{moduleCommonStrings.actions.cancel}</button>
        <div className="stepper-actions">
          {canGoBack && (
            <button type="button" className="btn-secondary" onClick={goToPreviousStep}>
              {moduleCommonStrings.actions.back}
            </button>
          )}
          {canGoNext ? (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={goToNextStep}>
              {moduleCommonStrings.actions.next}
            </button>
          ) : (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={handleSubmit} disabled={isSaving || !isStepSettled}>
              {isSaving ? moduleCommonStrings.actions.saving : fitnessStrings.form.save}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
